import { Link } from "react-router-dom";
import { SearchUser, SearchWork } from "@/lib/hooks/use-search";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { IconLoader2, IconMoodLookDown } from "@tabler/icons-react";
import { useState } from "react";

interface SearchDropdownProps {
  isOpen: boolean;
  isLoading: boolean;
  users: SearchUser[];
  works: SearchWork[];
  onClose: () => void;
  searchQuery: string;
}

// Component to handle premium badge display
function UserPremiumBadge({ userId }: { userId: string }) {
  const { data: isPremium } = useQuery({
    queryKey: ["searchProfileIsPremium", userId],
    queryFn: async () => {
      try {
        // Check the profiles table directly for the is_premium flag and premium_until
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("is_premium, premium_until")
          .eq("id", userId)
          .single();

        if (profileError) {
          return false;
        }

        // Check if user is premium and if premium_until is in the future (or null)
        return (
          !!profileData?.is_premium &&
          (!profileData.premium_until ||
            new Date(profileData.premium_until) > new Date())
        );
      } catch {
        return false;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (!isPremium) return null;

  return (
    <div className="flex items-center justify-center bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-md">
      Pro
    </div>
  );
}

export function SearchDropdown({
  isOpen,
  isLoading,
  users,
  works,
  onClose,
  searchQuery,
}: SearchDropdownProps) {
  // Track loading state for each image
  const [loadedAvatars, setLoadedAvatars] = useState<Record<string, boolean>>(
    {}
  );
  const [loadedThumbnails, setLoadedThumbnails] = useState<
    Record<string, boolean>
  >({});

  if (!isOpen) return null;

  const hasResults = users.length > 0 || works.length > 0;

  const handleAvatarLoad = (userId: string) => {
    setLoadedAvatars((prev) => ({ ...prev, [userId]: true }));
  };

  const handleThumbnailLoad = (workId: string) => {
    setLoadedThumbnails((prev) => ({ ...prev, [workId]: true }));
  };

  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 -ml-2 md:ml-0 md:left-0 md:right-0 md:translate-x-0 z-50 mt-2 w-[calc(100dvw-2rem)] md:w-full">
      <div className="w-full bg-white rounded-xl border border-light overflow-hidden pb-3">
        <div className="max-h-[80vh] overflow-y-auto scrollbar-hide">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <IconLoader2 size={28} className="animate-spin text-medium" />
            </div>
          ) : !hasResults ? (
            <div className="flex flex-col items-center justify-center py-10 space-y-2">
              <IconMoodLookDown size={28} className="text-medium" />
              <p className="text-base text-center text-medium">
                No results found
              </p>
            </div>
          ) : (
            <>
              {/* Profiles section */}
              {users.length > 0 && (
                <div className="py-2">
                  <div className="px-4 py-2">
                    <h3 className="text-base font-sb text-black">Profiles</h3>
                  </div>
                  <div>
                    {users.map((user) => (
                      <Link
                        key={user.id}
                        to={`/${user.username}`}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-sidebar-accent rounded-lg transition-colors mx-1"
                        onClick={onClose}
                      >
                        <div className="h-10 w-10 rounded-lg overflow-hidden">
                          <Avatar className="h-10 w-10">
                            {user.avatar_url && (
                              <>
                                {!loadedAvatars[user.id] && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                    <IconLoader2
                                      size={16}
                                      className="animate-spin text-medium"
                                    />
                                  </div>
                                )}
                                <AvatarImage
                                  src={user.avatar_url}
                                  alt={user.username}
                                  onLoad={() => handleAvatarLoad(user.id)}
                                  className={
                                    loadedAvatars[user.id]
                                      ? "opacity-100"
                                      : "opacity-0"
                                  }
                                />
                              </>
                            )}
                            <AvatarFallback className="text-sm font-[550]">
                              {user.username[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <div>
                          <div className="flex items-center gap-1">
                            <UserPremiumBadge userId={user.id} />
                            <p className="text-foreground font-sb">
                              {user.full_name || user.username}
                            </p>
                          </div>
                          <p className="text-sm text-medium">
                            @{user.username}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Works section */}
              {works.length > 0 && (
                <div className="py-2">
                  <div className="px-4 py-2">
                    <h3 className="text-base font-sb text-black">Works</h3>
                  </div>
                  <div>
                    {works.map((work) => (
                      <Link
                        key={work.id}
                        to={`/${work.username}/${work.slug}`}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-sidebar-accent rounded-lg transition-colors mx-1"
                        onClick={onClose}
                      >
                        <div className="h-10 w-10 rounded-lg overflow-hidden flex-shrink-0 relative">
                          {work.video_thumbnail_url || work.source_url ? (
                            <>
                              {!loadedThumbnails[work.id] && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                  <IconLoader2
                                    size={16}
                                    className="animate-spin text-medium"
                                  />
                                </div>
                              )}
                              <img
                                src={
                                  work.video_thumbnail_url ||
                                  work.source_url ||
                                  ""
                                }
                                alt={work.title}
                                className={`w-full h-full object-cover ${
                                  loadedThumbnails[work.id]
                                    ? "opacity-100"
                                    : "opacity-0"
                                }`}
                                onLoad={() => handleThumbnailLoad(work.id)}
                              />
                            </>
                          ) : (
                            <div className="bg-light w-full h-full flex items-center justify-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 text-medium"
                                width="44"
                                height="44"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path
                                  stroke="none"
                                  d="M0 0h24v24H0z"
                                  fill="none"
                                />
                                <path d="M15 8h.01" />
                                <path d="M3 6a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3v-12z" />
                                <path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5" />
                                <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-foreground font-sb">
                            {work.title}
                          </p>
                          <p className="text-sm text-medium">
                            @{work.username}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* View all results link */}
              {hasResults && (
                <div className="p-3">
                  <Link
                    to={`/search?q=${encodeURIComponent(searchQuery)}`}
                    className="block text-center w-full items-center justify-center rounded-full font-sb h-[44px] px-8 py-3 bg-black text-white hover:bg-black/90 transition-colors text-sm"
                    onClick={onClose}
                  >
                    View all results
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
